/**
 * PLAT-9968 — shared analytics hook fired when the composition quick-edit
 * popover opens. Used by both surfaces:
 *
 *   - `ClipTranscriptBridge` — transcript chip click path
 *   - `CompositionCanvasActionsLayer` — canvas iframe selection path
 *
 * Hosting both call sites here keeps the `(targetId, field_count)` dedupe
 * signature in one place so the two surfaces can't drift.
 *
 * The hook fires `trackCompositionQuickEditOpened` once per
 * `(targetId, field_count)` signature while `isOpen` is true. Re-opening
 * the same target re-fires (signature resets on close).
 */
import { useEffect, useRef } from 'react';

import {
  trackCompositionQuickEditOpened,
  type CompositionQuickEditSurface,
} from '../EventTracker';

import type { CompositionQuickEditField } from '@src/videoTranscriptionProcessor/translation/extractCompositionQuickEditFields';

interface Args {
  /** Whether the popover is currently mounted / open. */
  isOpen: boolean;
  /** The element id being edited; null when no target is resolved. */
  targetId: string | null;
  /**
   * Composition subtype for analytics slicing. `'overlay'` for standalone
   * overlays. Null when no target resolves.
   */
  compositionType:
    | 'introCard'
    | 'endCard'
    | 'nameCard'
    | 'overlay'
    | null;
  /** Fields the popover will show (used for `field_count`). */
  fields: ReadonlyArray<CompositionQuickEditField>;
  storyId: string | null;
  surface: CompositionQuickEditSurface;
}

export function useCompositionQuickEditOpenedTracking({
  isOpen,
  targetId,
  compositionType,
  fields,
  storyId,
  surface,
}: Args): void {
  const lastTrackedSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen || !targetId || !compositionType || fields.length === 0) {
      lastTrackedSignatureRef.current = null;
      return;
    }
    const signature = `${targetId}:${fields.length}`;
    if (lastTrackedSignatureRef.current === signature) return;
    lastTrackedSignatureRef.current = signature;

    trackCompositionQuickEditOpened({
      story_id: storyId ?? undefined,
      composition_element_id: targetId,
      composition_type: compositionType,
      surface,
      field_count: fields.length,
      has_translation: false,
      active_language: 'original',
    });
  }, [
    isOpen,
    targetId,
    compositionType,
    fields,
    storyId,
    surface,
  ]);
}
